import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import FieldLabel from './FieldLabel';
import { getUSStates } from '@/utils/InputValidator';

const US_STATE_OPTIONS = getUSStates().map((abbr) => ({ label: abbr, value: abbr }));

/**
 * Renders an `address` schema field (US). A composite of 5 stacked controls:
 * street1, street2, city, state (2-letter dropdown), and zip. The payload value
 * is `{ street1, street2, city, state, zip }`.
 */
const AddressField = ({ field, value, onChange, disabled, loggedInUser }) => {
    const v = value && typeof value === 'object' ? value : {};
    const config = field.config || {};
    const didAutofill = useRef(false);

    useEffect(() => {
        if (didAutofill.current) return;
        if (disabled || typeof onChange !== 'function') return;
        if (config.autofillLoggedIn === false || !loggedInUser) return;

        const hasValue = v.street1 || v.street2 || v.city || v.state || v.zip;
        if (hasValue) return;

        const raw = Array.isArray(loggedInUser.Address)
            ? loggedInUser.Address[0]
            : loggedInUser.Address || loggedInUser.address || null;
        if (!raw) return;

        didAutofill.current = true;
        onChange({
            street1: raw.street ?? '',
            street2: raw.streetLine2 ?? raw.street2 ?? '',
            city: raw.city ?? '',
            state: raw.state ?? '',
            zip: raw.zip ?? '',
        });
    }, [config.autofillLoggedIn, loggedInUser, v, onChange]);

    const update = (key, val) => onChange({ ...v, [key]: val });

    return (
        <View style={styles.container}>
            <FieldLabel
                label={field.label}
                required={field.required}
                description={field.description}
                testID={`form-field-${field.fieldId}-label`}
            />
            <Input
                testID={`form-field-${field.fieldId}-street1`}
                title="Street Address"
                placeholder="123 Main St"
                value={v.street1 ?? ''}
                onUpdate={(text) => update('street1', text)}
                required={field.required}
                disabled={disabled}
                autoCapitalize="words"
                autoComplete="address-line1"
            />
            <Input
                testID={`form-field-${field.fieldId}-street2`}
                title="Apartment, suite, etc. (optional)"
                placeholder="Apt 4B"
                value={v.street2 ?? ''}
                onUpdate={(text) => update('street2', text)}
                disabled={disabled}
                autoCapitalize="words"
                autoComplete="address-line2"
            />
            <View style={styles.cityStateZipRow}>
                <View style={styles.cityField}>
                    <Input
                        testID={`form-field-${field.fieldId}-city`}
                        title="City"
                        placeholder="City"
                        value={v.city ?? ''}
                        onUpdate={(text) => update('city', text)}
                        disabled={disabled}
                        autoCapitalize="words"
                        autoComplete="address-level2"
                    />
                </View>
                <View style={styles.stateField}>
                    <SelectInput
                        testID={`form-field-${field.fieldId}-state`}
                        title="State"
                        placeholder="State"
                        value={v.state ?? null}
                        onUpdate={(val) => update('state', val)}
                        options={US_STATE_OPTIONS}
                        disabled={disabled}
                        hideClearButton={disabled}
                    />
                </View>
                <View style={styles.zipField}>
                    <Input
                        testID={`form-field-${field.fieldId}-zip`}
                        title="ZIP Code"
                        placeholder="12345"
                        value={v.zip ?? ''}
                        onUpdate={(text) => update('zip', text)}
                        disabled={disabled}
                        type="numeric"
                        autoCapitalize="characters"
                        autoComplete="postal-code"
                    />
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ spacing }) => ({
    container: {
        marginVertical: spacing.sm,
    },
    cityStateZipRow: {
        flexDirection: 'row',
        gap: spacing.sm,
        alignItems: 'flex-start',
    },
    cityField: {
        flex: 2,
    },
    stateField: {
        width: 90,
    },
    zipField: {
        flex: 1,
    },
}));

export default AddressField;
